/**
 * Public self-signup. Creates an account in `pending_review` state and
 * sends a verification email. Login stays refused until both the user
 * clicks the verification link AND the operator approves the row from
 * the admin panel — distinct error codes from /v1/auth/login surface
 * in the LoginScreen so the user knows which step is pending.
 */

import {
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { useTurnstile } from "../lib/turnstile";

export function SignupScreen() {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `done` carries the server's auto_approved flag so the success copy
  // can reflect the actual back-end state (auto-approve on → "log in
  // after verify"; off → "wait for operator approval"). The earlier
  // hard-coded "wait for operator" copy was misleading whenever the
  // auto-approve flag was enabled in Settings.
  const [done, setDone] = useState<{ autoApproved: boolean } | null>(null);
  const turnstile = useTurnstile();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 12) {
      setError("הסיסמה חייבת להיות באורך 12 תווים לפחות.");
      return;
    }
    if (pw !== pw2) {
      setError("הסיסמאות אינן תואמות.");
      return;
    }
    if (turnstile.enabled && turnstile.token === null) {
      setError("נא להמתין לסיום בדיקת הבוטים ולנסות שוב.");
      return;
    }
    setBusy(true);
    try {
      const resp = await api.signup(email, pw, displayName, turnstile.token);
      setDone({ autoApproved: resp.auto_approved });
    } catch (err) {
      // After a failed submit, Turnstile tokens are single-use — reset
      // the widget so the next retry gets a fresh one.
      turnstile.reset();
      if (err instanceof ApiError) {
        if (err.detail === "email_already_taken") {
          setError("כתובת הדוא\"ל הזו כבר רשומה. נסה/י להתחבר או לאפס סיסמה.");
        } else if (err.detail === "signup_rate_limited") {
          setError("יותר מדי בקשות הרשמה. נסה/י שוב בעוד שעה.");
        } else if (err.detail === "turnstile_failed") {
          setError("בדיקת בוטים נכשלה. רענן את הדף ונסה/י שוב.");
        } else if (err.detail === "disposable_email_blocked") {
          setError("השתמש/י בכתובת דוא\"ל אמיתית (לא חד-פעמית).");
        } else {
          setError(err.detail);
        }
      } else {
        setError("משהו השתבש. נסה/י שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>הרשמה</Title>
          {done ? (
            <>
              <Alert color="green">
                {done.autoApproved
                  ? 'שלחנו אליך קישור אימות בדוא"ל (תקף 24 שעות). לחץ/י עליו ' +
                    "ותוכל/י להיכנס מיד לדשבורד."
                  : 'שלחנו אליך קישור אימות בדוא"ל (תקף 24 שעות). לחץ/י עליו, ' +
                    'ולאחר מכן המתן/י לאישור מפעיל המערכת. תקבל/י הודעת דוא"ל ' +
                    "נוספת כשהחשבון יאושר ויהיה זמין לכניסה."}
              </Alert>
              <Group justify="center">
                <Anchor component={Link} to="/login" size="sm">
                  חזרה למסך הכניסה
                </Anchor>
              </Group>
            </>
          ) : (
            <form onSubmit={submit}>
              <Stack>
                <Text c="dimmed" size="sm">
                  כדי להירשם, הזן/י כתובת דוא"ל ובחר/י סיסמה (לפחות 12 תווים).
                  נשלח אליך קישור אימות; אחריו תוכל/י להיכנס לדשבורד.
                </Text>
                <TextInput
                  label="כתובת דוא״ל"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  dir="ltr"
                />
                <TextInput
                  label="שם תצוגה"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
                <PasswordInput
                  label="סיסמה (לפחות 12 תווים)"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                />
                <PasswordInput
                  label="סיסמה (אישור)"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                />
                {turnstile.enabled && (
                  <div ref={turnstile.containerRef} style={{ minHeight: 65 }} />
                )}
                {error && <Alert color="red">{error}</Alert>}
                <Button
                  type="submit"
                  loading={busy}
                  fullWidth
                  disabled={
                    !email ||
                    !displayName ||
                    !pw ||
                    (turnstile.enabled && turnstile.token === null)
                  }
                >
                  הירשם
                </Button>
                <Group justify="center">
                  <Anchor component={Link} to="/login" size="sm">
                    כבר יש לי חשבון
                  </Anchor>
                </Group>
              </Stack>
            </form>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
